import type { apiClient } from '~/shared/api/data-ingestion-api'
import { formatDateDefault } from '~/shared/lib/datetime'
import { getIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { pluralize } from '~/shared/lib/pluralize'
import type { AuditTrailEventDescriptionField, PatientMatchEventProps } from '../../model'
import type { EventMeta } from '../pre-pass'
import { collectFieldSearchText } from './searchable-text-utils'

const NO_DATA_PLACEHOLDER = '--'

export function transformPatientMatch(
  event: apiClient.AuditEvent,
  filteredEvents: apiClient.AuditEvent[],
  meta: EventMeta,
  paPatientData?: apiClient.PatientPaRecord[],
): PatientMatchEventProps {
  let title: string
  let descriptionText: string | null = null
  let fields: AuditTrailEventDescriptionField[] | null = null

  if (event.event_type === 'patientlookup' || event.event_type === 'pa-patientlookup') {
    const patientMatchesCount = event.patient_case_candidates?.patient_data?.length ?? 0
    title =
      patientMatchesCount > 0
        ? `Matching: ${patientMatchesCount} ${pluralize(patientMatchesCount, 'match', 'es')} ${patientMatchesCount === 1 ? 'was' : 'were'} found in the system and provided for review`
        : 'Matching: No matches found'
  } else if (event.event_type === 'patientrev' || event.event_type === 'pa-patientrev') {
    title = meta.patientMatchRevalidated
      ? 'Matching: Match revalidated'
      : 'Matching: Match validated'

    const isPaEvent = event.event_type === 'pa-patientrev'
    const lookupType = isPaEvent ? 'pa-patientlookup' : 'patientlookup'

    const resolveMatch = (revEvent: apiClient.AuditEvent) => {
      const lookup = filteredEvents
        .filter((e) => e.event_type === lookupType && e.timestamp < revEvent.timestamp)
        .pop()
      const patient = filteredEvents
        .filter((e) => e.event_type === 'patient' && e.timestamp < revEvent.timestamp)
        .pop()
      let match = lookup?.patient_case_candidates?.patient_data?.find(
        (_, i) => i === revEvent.index,
      )
      let newMatch = false
      if (!match) {
        match = patient?.patient_case_candidates?.patient_data?.find((_, i) => i === revEvent.index)
        newMatch = true
      }
      if (!match && isPaEvent && revEvent.index != null && paPatientData?.[revEvent.index]) {
        const live = paPatientData[revEvent.index]
        match = {
          patient_first_name: live.patient_first_name,
          patient_last_name: live.patient_last_name,
          date_of_birth: live.patient_dob,
          payorname: live.payor_id,
          test_name: live.test_name,
          lims_date_of_service: live.service_date,
          lims_casefile_id: live.case_id,
          policy_number: live.policy_number,
        } as apiClient.PatientRecordResponse
      }
      return { match, newMatch }
    }

    const { match: patientMatch, newMatch: isNewMatch } = resolveMatch(event)

    if (patientMatch) {
      const paRecord = isPaEvent ? paPatientData?.[event.index ?? -1] : undefined
      const pa = isPaEvent
        ? (patientMatch as Record<string, unknown>)
        : ({} as Record<string, unknown>)
      descriptionText = isNewMatch ? 'New match created' : null

      const fieldVal = (
        label: string,
        value: string | null | undefined,
      ): AuditTrailEventDescriptionField => ({
        label,
        values: [{ diff: [value || NO_DATA_PLACEHOLDER] }],
      })

      const dateVal = (label: string, ...sources: (string | null | undefined)[]) => {
        const raw = sources.find(Boolean)
        return fieldVal(label, raw ? formatDateDefault(raw) : NO_DATA_PLACEHOLDER)
      }

      const allFields: AuditTrailEventDescriptionField[] = [
        fieldVal(
          'Patient Name',
          [patientMatch.patient_first_name, patientMatch.patient_last_name]
            .filter(Boolean)
            .join(' '),
        ),
        dateVal(
          'Birth Date',
          patientMatch.date_of_birth,
          pa.patient_dob as string,
          paRecord?.patient_dob,
        ),
        fieldVal(
          'Payor Name',
          patientMatch.payorname || (pa.payor_id as string) || paRecord?.payor_id,
        ),
        fieldVal('Test Name', patientMatch.test_name),
        dateVal(
          'Date of Service',
          patientMatch.lims_date_of_service,
          pa.service_date as string,
          paRecord?.service_date,
        ),
        ...(isPaEvent
          ? []
          : [
              fieldVal(
                getIsFeatureEnabled('2026-10-claim-card-redesign')
                  ? 'Payor Claim Number'
                  : getIsFeatureEnabled('2026-06-carrier-claim-id')
                    ? 'Natera Claim ID'
                    : 'Claim ID',
                patientMatch.claim_id,
              ),
              fieldVal('Visit ID', patientMatch.visitfid || (pa.care_id as string)),
            ]),
        ...(isPaEvent
          ? [
              fieldVal(
                'Reference Number',
                (pa.reference_token as string) || paRecord?.reference_token,
              ),
              fieldVal(
                'Authorization Code',
                (pa.authorization_token as string) || paRecord?.authorization_token,
              ),
              fieldVal('CPT Code(s)', (pa.cpt_codes as string) || paRecord?.cpt_codes),
            ]
          : []),
        fieldVal(
          isPaEvent ? 'LIMS Case ID' : 'Case Number',
          patientMatch.lims_casefile_id || (pa.case_id as string) || paRecord?.case_id,
        ),
        fieldVal('Member ID', patientMatch.policy_number),
        ...(!isPaEvent && getIsFeatureEnabled('2026-10-claim-card-redesign')
          ? [
              fieldVal('CPT Codes Billed', patientMatch.allproccodes_claim),
              fieldVal('Billed Amount', patientMatch.billprice),
              dateVal(
                'Appeal Date',
                (patientMatch as Record<string, unknown>).appeal_date as string,
              ),
            ]
          : []),
        ...(isPaEvent
          ? [fieldVal('PA Vendor', (pa.vendor_given_to as string) || paRecord?.vendor_given_to)]
          : []),
      ]

      if (meta.patientMatchRevalidated) {
        const prevRevEvent = filteredEvents
          .filter((e) => e.event_type === event.event_type && e.timestamp < event.timestamp)
          .pop()
        const { match: prevMatch } = prevRevEvent
          ? resolveMatch(prevRevEvent)
          : { match: undefined }

        if (prevMatch) {
          const prevPaRecord = isPaEvent ? paPatientData?.[prevRevEvent!.index ?? -1] : undefined
          const prevPa = isPaEvent
            ? (prevMatch as Record<string, unknown>)
            : ({} as Record<string, unknown>)

          const prevFieldVal = (value: string | null | undefined) => value || NO_DATA_PLACEHOLDER
          const prevDateVal = (...sources: (string | null | undefined)[]) => {
            const raw = sources.find(Boolean)
            return raw ? formatDateDefault(raw) : NO_DATA_PLACEHOLDER
          }

          const prevValues: Record<string, string> = {
            'Patient Name': prevFieldVal(
              [prevMatch.patient_first_name, prevMatch.patient_last_name].filter(Boolean).join(' '),
            ),
            'Birth Date': prevDateVal(
              prevMatch.date_of_birth,
              prevPa.patient_dob as string,
              prevPaRecord?.patient_dob,
            ),
            'Payor Name': prevFieldVal(
              prevMatch.payorname || (prevPa.payor_id as string) || prevPaRecord?.payor_id,
            ),
            'Test Name': prevFieldVal(prevMatch.test_name),
            'Date of Service': prevDateVal(
              prevMatch.lims_date_of_service,
              prevPa.service_date as string,
              prevPaRecord?.service_date,
            ),
            'LIMS Case ID': prevFieldVal(
              prevMatch.lims_casefile_id || (prevPa.case_id as string) || prevPaRecord?.case_id,
            ),
            'Case Number': prevFieldVal(
              prevMatch.lims_casefile_id || (prevPa.case_id as string) || prevPaRecord?.case_id,
            ),
            'Member ID': prevFieldVal(prevMatch.policy_number),
            'Reference Number': prevFieldVal(
              (prevPa.reference_token as string) || prevPaRecord?.reference_token,
            ),
            'Authorization Code': prevFieldVal(
              (prevPa.authorization_token as string) || prevPaRecord?.authorization_token,
            ),
            'CPT Code(s)': prevFieldVal((prevPa.cpt_codes as string) || prevPaRecord?.cpt_codes),
            'PA Vendor': prevFieldVal(
              (prevPa.vendor_given_to as string) || prevPaRecord?.vendor_given_to,
            ),
            'Claim ID': prevFieldVal(prevMatch.claim_id),
            'Natera Claim ID': prevFieldVal(prevMatch.claim_id),
            'Payor Claim Number': prevFieldVal(prevMatch.claim_id),
            'Visit ID': prevFieldVal(prevMatch.visitfid || (prevPa.care_id as string)),
            'CPT Codes Billed': prevFieldVal(prevMatch.allproccodes_claim),
            'Billed Amount': prevFieldVal(prevMatch.billprice),
            'Appeal Date': prevDateVal(
              (prevMatch as Record<string, unknown>).appeal_date as string,
            ),
          }

          fields = allFields
            .filter((f) => {
              const curVal = f.values[0]?.diff?.[0] ?? NO_DATA_PLACEHOLDER
              const prevVal = prevValues[f.label] ?? NO_DATA_PLACEHOLDER
              return curVal !== prevVal
            })
            .map((f) => ({
              label: f.label,
              values: [
                {
                  previous: prevValues[f.label] ?? NO_DATA_PLACEHOLDER,
                  current: f.values[0]?.diff?.[0] ?? NO_DATA_PLACEHOLDER,
                },
              ],
            }))
        } else {
          fields = allFields
        }
      } else {
        fields = allFields
      }
    } else if (event.status) {
      if (event.status === 'validated') {
        descriptionText = 'Match validated'
      } else if (event.status === 'no_match') {
        descriptionText = 'No match selected'
      }
    }
  } else {
    title = event.event_type
  }

  return {
    type: 'patient-match',
    timestamp: event.timestamp,
    user: event.user,
    userRole: event.user ? 'person' : 'opti',
    step: 'patient',
    eventType: event.event_type,
    title,
    descriptionText,
    fields,
    notes: event.notes ?? null,
  }
}

export function getPatientMatchSearchableText(props: PatientMatchEventProps): string {
  const parts = [props.title]
  if (props.descriptionText) parts.push(props.descriptionText)
  if (props.fields) parts.push(collectFieldSearchText(props.fields))
  if (props.notes) parts.push(props.notes)
  return parts.join(' ')
}
